/**
 * TransactionReview.tsx
 * AI-Assisted Transaction Review — Bookkeeper Workflow
 *
 * Layout:
 * - Client selector + period picker
 * - Summary bar: Total | Matched | Needs Review | Open | Resolved
 * - Tab: "Needs Review" (unmatched + new_account) | "Matched" | "Resolved" | "Audit Log"
 * - Each transaction card shows:
 *   - Vendor, description, amount, date
 *   - Current account code (if any)
 *   - AI suggested code + confidence badge
 *   - Approve / Override / Skip buttons
 */

import { useState, useMemo, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  ChevronRight,
  Sparkles,
  BookOpen,
  BarChart3,
  Clock,
  ArrowUpDown,
  Upload,
} from "lucide-react";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function currentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function formatAmount(cents: number) {
  const abs = Math.abs(cents) / 100;
  const formatted = abs.toLocaleString("en-US", { style: "currency", currency: "USD" });
  return cents < 0 ? `(${formatted})` : formatted;
}

function confidenceColor(confidence: number) {
  if (confidence >= 80) return "text-emerald-600 bg-emerald-50 border-emerald-200";
  if (confidence >= 60) return "text-amber-600 bg-amber-50 border-amber-200";
  return "text-red-600 bg-red-50 border-red-200";
}

function matchStatusBadge(status: string) {
  switch (status) {
    case "matched":
      return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Matched</Badge>;
    case "new_account":
      return <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">New Account</Badge>;
    default:
      return <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">Unmatched</Badge>;
  }
}

function reviewStatusBadge(status: string) {
  switch (status) {
    case "approved":
      return <Badge className="bg-emerald-100 text-emerald-700 text-xs">Approved</Badge>;
    case "overridden":
      return <Badge className="bg-blue-100 text-blue-700 text-xs">Overridden</Badge>;
    case "skipped":
      return <Badge className="bg-gray-100 text-gray-600 text-xs">Skipped</Badge>;
    default:
      return <Badge className="bg-orange-100 text-orange-700 text-xs">Open</Badge>;
  }
}

// ─── Override Dialog ──────────────────────────────────────────────────────────

interface OverrideDialogProps {
  open: boolean;
  onClose: () => void;
  transaction: {
    id: number;
    coreClientId: number;
    contactName?: string | null;
    description?: string | null;
    amount: number;
    accountCode?: string | null;
    accountName?: string | null;
    aiSuggestedCode?: string | null;
    aiSuggestedName?: string | null;
  };
  resolvedBy: string;
  resolvedByRole: string;
  onResolved: () => void;
}

function OverrideDialog({
  open,
  onClose,
  transaction,
  resolvedBy,
  resolvedByRole,
  onResolved,
}: OverrideDialogProps) {
  const [code, setCode] = useState(transaction.aiSuggestedCode ?? transaction.accountCode ?? "");
  const [name, setName] = useState(transaction.aiSuggestedName ?? transaction.accountName ?? "");
  const [notes, setNotes] = useState("");

  const resolve = trpc.transactionReview.resolve.useMutation({
    onSuccess: () => {
      toast.success("Transaction resolved");
      onResolved();
      onClose();
    },
    onError: (err) => toast.error(`Failed: ${err.message}`),
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Override Account Code</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="bg-muted/40 rounded-lg p-3 text-sm space-y-1">
            <div className="font-medium">{transaction.contactName ?? "Unknown Vendor"}</div>
            <div className="text-muted-foreground">{transaction.description ?? "No description"}</div>
            <div className="font-semibold">{formatAmount(transaction.amount)}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Account Code</Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="e.g. 400"
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Account Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Advertising"
                className="h-8 text-sm"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for override..."
              className="text-sm h-20 resize-none"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            disabled={!code || !name || resolve.isPending}
            onClick={() =>
              resolve.mutate({
                transactionReviewId: transaction.id,
                coreClientId: transaction.coreClientId,
                action: "overridden",
                finalCode: code,
                finalName: name,
                notes: notes || undefined,
                resolvedBy,
                resolvedByRole,
              })
            }
          >
            {resolve.isPending ? "Saving..." : "Save Override"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Transaction Card ─────────────────────────────────────────────────────────

interface TransactionCardProps {
  tx: {
    id: number;
    coreClientId: number;
    xeroTxId: string;
    txDate: string;
    description?: string | null;
    reference?: string | null;
    contactName?: string | null;
    amount: number;
    accountCode?: string | null;
    accountName?: string | null;
    matchStatus: string;
    aiSuggestedCode?: string | null;
    aiSuggestedName?: string | null;
    aiConfidence?: number | null;
    reviewStatus: string;
    reviewedBy?: string | null;
    reviewedAt?: Date | null;
  };
  resolvedBy: string;
  resolvedByRole: string;
  onResolved: () => void;
  showActions: boolean;
}

function TransactionCard({ tx, resolvedBy, resolvedByRole, onResolved, showActions }: TransactionCardProps) {
  const [overrideOpen, setOverrideOpen] = useState(false);

  const resolve = trpc.transactionReview.resolve.useMutation({
    onSuccess: () => {
      toast.success("Transaction resolved");
      onResolved();
    },
    onError: (err) => toast.error(`Failed: ${err.message}`),
  });

  const syncToXero = trpc.transactionReview.syncToXero.useMutation({
    onSuccess: () => toast.success("Synced to Xero"),
    onError: (err) => toast.error(`Sync failed: ${err.message}`),
  });

  return (
    <>
      <div className="border rounded-lg p-4 bg-card hover:bg-muted/20 transition-colors">
        <div className="flex items-start justify-between gap-4">
          {/* Left: Transaction info */}
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm truncate">
                {tx.contactName ?? "Unknown Vendor"}
              </span>
              {matchStatusBadge(tx.matchStatus)}
              {reviewStatusBadge(tx.reviewStatus)}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {tx.description ?? tx.reference ?? "No description"} · {tx.txDate}
            </div>
            {/* Current account code */}
            {tx.accountCode && (
              <div className="text-xs text-muted-foreground">
                Current: <span className="font-mono text-foreground">{tx.accountCode}</span>
                {tx.accountName && ` — ${tx.accountName}`}
              </div>
            )}
          </div>

          {/* Right: Amount */}
          <div className="text-right shrink-0">
            <div className={`font-bold text-sm ${tx.amount < 0 ? "text-red-600" : "text-emerald-600"}`}>
              {formatAmount(tx.amount)}
            </div>
          </div>
        </div>

        {/* AI Suggestion */}
        {tx.aiSuggestedCode && tx.reviewStatus === "open" && (
          <div className={`mt-3 flex items-center gap-2 rounded-md border px-3 py-2 text-xs ${confidenceColor(tx.aiConfidence ?? 0)}`}>
            <Sparkles className="w-3.5 h-3.5 shrink-0" />
            <span>
              AI suggests: <strong>{tx.aiSuggestedCode}</strong> — {tx.aiSuggestedName}
            </span>
            <span className="ml-auto font-semibold">{tx.aiConfidence}% confident</span>
          </div>
        )}

        {/* Resolved info */}
        {tx.reviewStatus !== "open" && tx.reviewedBy && (
          <div className="mt-2 text-xs text-muted-foreground">
            Resolved by {tx.reviewedBy} · {tx.accountCode} — {tx.accountName}
          </div>
        )}

        {/* Actions */}
        {showActions && tx.reviewStatus === "open" && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {tx.aiSuggestedCode && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                disabled={resolve.isPending}
                onClick={() =>
                  resolve.mutate({
                    transactionReviewId: tx.id,
                    coreClientId: tx.coreClientId,
                    action: "approved",
                    finalCode: tx.aiSuggestedCode!,
                    finalName: tx.aiSuggestedName!,
                    resolvedBy,
                    resolvedByRole,
                  })
                }
              >
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Approve AI
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => setOverrideOpen(true)}
            >
              <ArrowUpDown className="w-3 h-3 mr-1" />
              Override
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-muted-foreground"
              disabled={resolve.isPending}
              onClick={() =>
                resolve.mutate({
                  transactionReviewId: tx.id,
                  coreClientId: tx.coreClientId,
                  action: "skipped",
                  finalCode: tx.accountCode ?? "SKIP",
                  finalName: tx.accountName ?? "Skipped",
                  resolvedBy,
                  resolvedByRole,
                })
              }
            >
              <XCircle className="w-3 h-3 mr-1" />
              Skip
            </Button>
          </div>
        )}

        {/* Sync to Xero for resolved */}
        {tx.reviewStatus !== "open" && tx.reviewStatus !== "skipped" && (
          <div className="mt-2">
            <Button
              size="sm"
              variant="ghost"
              className="h-6 text-xs text-blue-600 hover:text-blue-700"
              disabled={syncToXero.isPending}
              onClick={() => syncToXero.mutate({ transactionReviewId: tx.id })}
            >
              <Upload className="w-3 h-3 mr-1" />
              {syncToXero.isPending ? "Syncing..." : "Sync to Xero"}
            </Button>
          </div>
        )}
      </div>

      {overrideOpen && (
        <OverrideDialog
          open={overrideOpen}
          onClose={() => setOverrideOpen(false)}
          transaction={tx}
          resolvedBy={resolvedBy}
          resolvedByRole={resolvedByRole}
          onResolved={onResolved}
        />
      )}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function TransactionReview() {
  const { user } = useAuth();
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [period, setPeriod] = useState(currentPeriod());
  const [activeTab, setActiveTab] = useState("needs-review");
  const [search, setSearch] = useState("");

  // Pre-select client from URL ?clientId= param (navigated from Xero|Manus Active / anomaly Review button)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const clientIdParam = params.get("clientId");
    if (clientIdParam) {
      const id = Number(clientIdParam);
      if (!isNaN(id) && id > 0) {
        setSelectedClientId(id);
        setActiveTab("needs-review");
      }
    }
  }, []);

  // Load client list from analytics summaries
  const analyticsQuery = trpc.analytics.getSummaries.useQuery({ period });
  const clients = useMemo(() => {
    if (analyticsQuery.data) {
      return analyticsQuery.data.map((c: { clientId: number; clientName: string; practiceKey: string }) => ({
        id: c.clientId,
        name: c.clientName,
        practice: c.practiceKey,
      }));
    }
    return [];
  }, [analyticsQuery.data]);

  // Pull transactions mutation
  const pullMutation = trpc.transactionReview.pull.useMutation({
    onSuccess: (result) => {
      toast.success(
        `Pulled ${result.total} transactions — ${result.matched} matched, ${result.unmatched + result.newAccount} need review`
      );
      reviewsQuery.refetch();
      summaryQuery.refetch();
    },
    onError: (err) => toast.error(`Pull failed: ${err.message}`),
  });

  // Reviews query
  const reviewsQuery = trpc.transactionReview.getReviews.useQuery(
    { clientId: selectedClientId ?? 0, period },
    { enabled: !!selectedClientId }
  );

  // Summary query
  const summaryQuery = trpc.transactionReview.getSummary.useQuery(
    { clientId: selectedClientId ?? 0, period },
    { enabled: !!selectedClientId }
  );

  // Resolution log
  const logQuery = trpc.transactionReview.getResolutionLog.useQuery(
    { clientId: selectedClientId ?? 0 },
    { enabled: !!selectedClientId && activeTab === "audit-log" }
  );

  const reviews = reviewsQuery.data ?? [];
  const summary = summaryQuery.data;

  // Filter reviews by tab and search
  const filteredReviews = useMemo(() => {
    let filtered = reviews;
    if (activeTab === "needs-review") {
      filtered = reviews.filter(
        (r) => r.reviewStatus === "open" && (r.matchStatus === "unmatched" || r.matchStatus === "new_account")
      );
    } else if (activeTab === "matched") {
      filtered = reviews.filter((r) => r.matchStatus === "matched");
    } else if (activeTab === "resolved") {
      filtered = reviews.filter((r) => r.reviewStatus !== "open");
    }
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.contactName?.toLowerCase().includes(q) ||
          r.description?.toLowerCase().includes(q) ||
          r.accountCode?.toLowerCase().includes(q) ||
          r.aiSuggestedCode?.toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [reviews, activeTab, search]);

  const resolvedBy = user?.name ?? user?.email ?? "Unknown";
  const resolvedByRole = user?.role ?? "bookkeeper";

  const handleResolved = () => {
    reviewsQuery.refetch();
    summaryQuery.refetch();
  };

  return (
    <div className="px-32 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Transaction Review</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            AI-assisted account code matching — review and resolve unmatched transactions
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-end gap-4 flex-wrap">
        <div className="space-y-1 min-w-[240px]">
          <Label className="text-xs font-medium">Client</Label>
          <Select
            value={selectedClientId?.toString() ?? ""}
            onValueChange={(v) => setSelectedClientId(Number(v))}
          >
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder="Select a client..." />
            </SelectTrigger>
            <SelectContent>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id.toString()}>
                  {c.name}
                  <span className="ml-2 text-xs text-muted-foreground">{c.practice}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs font-medium">Period</Label>
          <Input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="h-9 text-sm w-40"
          />
        </div>

        <Button
          size="sm"
          disabled={!selectedClientId || pullMutation.isPending}
          onClick={() =>
            selectedClientId &&
            pullMutation.mutate({ clientId: selectedClientId, period })
          }
          className="h-9"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${pullMutation.isPending ? "animate-spin" : ""}`} />
          {pullMutation.isPending ? "Pulling..." : "Pull from Xero"}
        </Button>
      </div>

      {/* Summary Bar */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: "Total", value: summary.total, icon: BarChart3, color: "text-foreground" },
            { label: "Matched", value: summary.matched, icon: CheckCircle2, color: "text-emerald-600" },
            { label: "New Account", value: summary.newAccount, icon: AlertTriangle, color: "text-amber-600" },
            { label: "Unmatched", value: summary.unmatched, icon: XCircle, color: "text-red-600" },
            { label: "Open", value: summary.open, icon: Clock, color: "text-orange-600" },
          ].map(({ label, value, icon: Icon, color }) => (
            <Card key={label} className="border shadow-sm">
              <CardContent className="p-3 flex items-center gap-3">
                <Icon className={`w-5 h-5 ${color} shrink-0`} />
                <div>
                  <div className={`text-xl font-bold ${color}`}>{value}</div>
                  <div className="text-xs text-muted-foreground">{label}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Main Content */}
      {selectedClientId ? (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <TabsList className="h-9">
              <TabsTrigger value="needs-review" className="text-xs">
                Needs Review
                {summary && summary.open > 0 && (
                  <span className="ml-1.5 bg-orange-500 text-white rounded-full px-1.5 py-0.5 text-[10px] font-bold">
                    {summary.open}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="matched" className="text-xs">Matched</TabsTrigger>
              <TabsTrigger value="resolved" className="text-xs">Resolved</TabsTrigger>
              <TabsTrigger value="audit-log" className="text-xs">Audit Log</TabsTrigger>
            </TabsList>

            <Input
              placeholder="Search vendor, description, code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-xs w-64"
            />
          </div>

          {/* Needs Review Tab */}
          <TabsContent value="needs-review" className="mt-4">
            {reviewsQuery.isLoading ? (
              <div className="text-center py-12 text-muted-foreground text-sm">Loading transactions...</div>
            ) : filteredReviews.length === 0 ? (
              <div className="text-center py-12 space-y-2">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
                <div className="font-medium">All caught up!</div>
                <div className="text-sm text-muted-foreground">
                  No transactions need review for this period.
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground mb-3">
                  {filteredReviews.length} transaction{filteredReviews.length !== 1 ? "s" : ""} need attention
                </div>
                {filteredReviews.map((tx) => (
                  <TransactionCard
                    key={tx.id}
                    tx={tx}
                    resolvedBy={resolvedBy}
                    resolvedByRole={resolvedByRole}
                    onResolved={handleResolved}
                    showActions={true}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Matched Tab */}
          <TabsContent value="matched" className="mt-4">
            {filteredReviews.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">
                No matched transactions for this period.
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground mb-3">
                  {filteredReviews.length} matched transaction{filteredReviews.length !== 1 ? "s" : ""}
                </div>
                {filteredReviews.map((tx) => (
                  <TransactionCard
                    key={tx.id}
                    tx={tx}
                    resolvedBy={resolvedBy}
                    resolvedByRole={resolvedByRole}
                    onResolved={handleResolved}
                    showActions={false}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Resolved Tab */}
          <TabsContent value="resolved" className="mt-4">
            {filteredReviews.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">
                No resolved transactions yet.
              </div>
            ) : (
              <div className="space-y-2">
                {filteredReviews.map((tx) => (
                  <TransactionCard
                    key={tx.id}
                    tx={tx}
                    resolvedBy={resolvedBy}
                    resolvedByRole={resolvedByRole}
                    onResolved={handleResolved}
                    showActions={false}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit-log" className="mt-4">
            {logQuery.isLoading ? (
              <div className="text-center py-8 text-muted-foreground text-sm">Loading audit log...</div>
            ) : !logQuery.data || logQuery.data.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">
                No resolutions recorded yet.
              </div>
            ) : (
              <div className="space-y-2">
                {logQuery.data.map((log) => (
                  <div key={log.id} className="border rounded-lg p-3 text-sm bg-card">
                    <div className="flex items-center justify-between gap-4">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{log.resolvedBy}</span>
                          <Badge variant="outline" className="text-xs">{log.action}</Badge>
                          {log.syncedToXero && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">Synced to Xero</Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {log.originalCode && log.originalCode !== log.finalCode ? (
                            <>
                              <span className="line-through">{log.originalCode}</span>
                              <ChevronRight className="w-3 h-3 inline mx-1" />
                            </>
                          ) : null}
                          <span className="font-mono text-foreground">{log.finalCode}</span>
                          {log.finalName && ` — ${log.finalName}`}
                        </div>
                        {log.notes && (
                          <div className="text-xs text-muted-foreground italic">"{log.notes}"</div>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground shrink-0">
                        {new Date(log.createdAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      ) : (
        <div className="text-center py-20 space-y-3">
          <BookOpen className="w-12 h-12 text-muted-foreground/40 mx-auto" />
          <div className="font-medium text-muted-foreground">Select a client to begin</div>
          <div className="text-sm text-muted-foreground/70">
            Choose a client above, then click "Pull from Xero" to fetch and review their transactions.
          </div>
        </div>
      )}
    </div>
  );
}
